/**
 * Componente: RequestsTab
 * Descrição: Aba de requisições API para gerenciamento de tenant
 * 
 * Exibe dados de observabilidade filtrados pelo tenant selecionado:
 * - KPIs: Total, Taxa de Sucesso, Latência, Providers
 * - Gráficos: Volume/hora, Latência/provider
 * - Tabela de logs com filtros e paginação
 * - Modal de detalhes de requisição
 * 
 * @version 1.0.0
 * @release 2026-01-19
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Activity, 
  ArrowDownLeft, 
  ArrowUpRight, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle, 
  Search, 
  Eye, 
  Phone,
  RefreshCw,
} from 'lucide-react';
import { 
  useTenantObservabilityStats,
  useTenantApiRequestLogs,
  useTenantRequestVolume,
  useTenantLatencyByProvider,
} from '@/hooks/useTenantObservability';
import type { ObservabilityFilters, ApiRequestLog } from '@/hooks/useObservability';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

// ============= Interfaces =============

interface RequestsTabProps {
  /** ID do tenant para filtrar requisições */
  tenantId: string;
  /** Estado de carregamento externo */
  isLoading?: boolean;
}

// ============= Componente Principal =============

export function RequestsTab({ tenantId, isLoading: externalLoading }: RequestsTabProps) {
  // Estado de filtros (sem tenantId pois é fixo)
  const [filters, setFilters] = useState<Omit<ObservabilityFilters, 'tenantId'>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const [phoneSearchInput, setPhoneSearchInput] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedLog, setSelectedLog] = useState<ApiRequestLog | null>(null);

  // Hooks específicos do tenant
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useTenantObservabilityStats(tenantId, 24);
  const { data: logsResult, isLoading: logsLoading, refetch: refetchLogs } = useTenantApiRequestLogs(tenantId, filters, page, pageSize);
  const { data: volumeData, isLoading: volumeLoading } = useTenantRequestVolume(tenantId, 24);
  const { data: latencyData, isLoading: latencyLoading } = useTenantLatencyByProvider(tenantId, 24);

  // Cores para gráficos
  const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

  // Handlers
  const handleRefresh = () => {
    refetchStats();
    refetchLogs();
    setLastUpdate(new Date());
    toast.success('Dados atualizados');
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
    setPage(1);
  };

  const handlePhoneSearch = () => {
    setFilters(prev => ({ ...prev, phone: phoneSearchInput }));
    setPage(1);
  };

  const handleFilterChange = (key: keyof Omit<ObservabilityFilters, 'tenantId'>, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // Renderização de badges
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Badge variant="default" className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle2 className="w-3 h-3 mr-1" />Sucesso</Badge>;
      case 'error':
        return <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20"><XCircle className="w-3 h-3 mr-1" />Erro</Badge>;
      case 'timeout':
        return <Badge variant="secondary" className="bg-orange-500/10 text-orange-600 border-orange-500/20"><AlertTriangle className="w-3 h-3 mr-1" />Timeout</Badge>;
      default:
        return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
    }
  };

  const renderProviderBadge = (provider: string) => {
    // Mapa de cores por provider - ordenado por volume de uso
    const colors: Record<string, string> = {
      'z-api': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      'meta-whatsapp': 'bg-emerald-700/10 text-emerald-700 border-emerald-700/20',
      'waha': 'bg-teal-500/10 text-teal-600 border-teal-500/20',
      'notificame': 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      'meta-instagram': 'bg-pink-500/10 text-pink-600 border-pink-500/20',
      'webchat': 'bg-slate-500/10 text-slate-600 border-slate-500/20',
      'evolution': 'bg-green-500/10 text-green-600 border-green-500/20',
      'chatwoot': 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    };
    return <Badge variant="outline" className={colors[provider] || ''}>{provider}</Badge>;
  };

  const renderDirectionBadge = (direction: string) => {
    if (direction === 'inbound') {
      return <Badge variant="outline" className="bg-cyan-500/10 text-cyan-600"><ArrowDownLeft className="w-3 h-3 mr-1" />Entrada</Badge>;
    }
    return <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600"><ArrowUpRight className="w-3 h-3 mr-1" />Saída</Badge>;
  };

  // Loading state
  if (externalLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-24 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[250px] w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com última atualização */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Monitoramento de requisições API deste tenant
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Última atualização: {formatDistanceToNow(lastUpdate, { addSuffix: true, locale: ptBR })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requisições (24h)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : (stats?.totalRequests24h || 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.inboundCount || 0} entrada / {stats?.outboundCount || 0} saída
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {statsLoading ? '...' : `${(stats?.successRate || 0).toFixed(1)}%`}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.errorRate ? `${stats.errorRate.toFixed(1)}% erros` : 'Sem erros'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Latência Média</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : `${Math.round(stats?.avgLatencyMs || 0)}ms`}
            </div>
            <p className="text-xs text-muted-foreground">
              Tempo de resposta
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Providers Ativos</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statsLoading ? '...' : (stats?.byProvider?.length || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.byProvider?.map(p => p.provider).join(', ') || 'Nenhum'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Volume por Hora */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Volume de Requisições (24h)</CardTitle>
            <CardDescription>Requisições por hora</CardDescription>
          </CardHeader>
          <CardContent>
            {volumeLoading ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">Carregando...</div>
            ) : !volumeData?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">Sem dados no período</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={volumeData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="hour" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="success" name="Sucesso" fill="hsl(var(--chart-1))" stackId="a" />
                  <Bar dataKey="error" name="Erro" fill="hsl(var(--destructive))" stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Latência por Provider */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latência por Provider</CardTitle>
            <CardDescription>Tempo de resposta médio e P95</CardDescription>
          </CardHeader>
          <CardContent>
            {latencyLoading ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">Carregando...</div>
            ) : !latencyData?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">Sem dados no período</div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={latencyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" unit="ms" className="text-xs" />
                  <YAxis type="category" dataKey="provider" className="text-xs" width={80} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="avgLatency" name="Média" fill="hsl(var(--chart-2))" />
                  <Bar dataKey="p95Latency" name="P95" fill="hsl(var(--chart-3))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribuição por Origem */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por Origem</CardTitle>
            <CardDescription>Chat, Campanhas, Flows, Webhooks</CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading || !stats?.bySource?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                {statsLoading ? 'Carregando...' : 'Sem dados'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.bySource}
                    dataKey="count"
                    nameKey="source"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ source, percent }) => `${source} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {stats.bySource.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribuição por Provider */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Distribuição por Provider</CardTitle>
            <CardDescription>Volume de requisições por provider</CardDescription>
          </CardHeader>
          <CardContent>
            {statsLoading || !stats?.byProvider?.length ? (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                {statsLoading ? 'Carregando...' : 'Sem dados'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.byProvider}
                    dataKey="count"
                    nameKey="provider"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ provider, percent }) => `${provider} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {stats.byProvider.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Tabela de Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Logs de Requisições</CardTitle>
          <CardDescription>Histórico detalhado de operações com providers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filtros - SEM Tenant (já fixo) */}
          <div className="flex flex-wrap gap-2">
            <Select value={filters.provider || 'all'} onValueChange={(v) => handleFilterChange('provider', v)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Providers</SelectItem>
                <SelectItem value="z-api">Z-API</SelectItem>
                <SelectItem value="meta-whatsapp">Meta WhatsApp</SelectItem>
                <SelectItem value="waha">WAHA</SelectItem>
                <SelectItem value="notificame">Notificame</SelectItem>
                <SelectItem value="meta-instagram">Meta Instagram</SelectItem>
                <SelectItem value="webchat">Webchat</SelectItem>
                <SelectItem value="evolution">Evolution</SelectItem>
                <SelectItem value="chatwoot">Chatwoot</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.status || 'all'} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="success">Sucesso</SelectItem>
                <SelectItem value="error">Erro</SelectItem>
                <SelectItem value="timeout">Timeout</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.source || 'all'} onValueChange={(v) => handleFilterChange('source', v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Origem" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Origens</SelectItem>
                <SelectItem value="chat">Chat</SelectItem>
                <SelectItem value="campaign">Campanha</SelectItem>
                <SelectItem value="flow">Flow</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.direction || 'all'} onValueChange={(v) => handleFilterChange('direction', v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Direção" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas Direções</SelectItem>
                <SelectItem value="inbound">Entrada</SelectItem>
                <SelectItem value="outbound">Saída</SelectItem>
              </SelectContent>
            </Select>

            {/* Busca por erro */}
            <div className="flex items-center gap-1">
              <Input
                placeholder="Buscar por erro..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-[200px]"
              />
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>

            {/* Busca por telefone */}
            <div className="flex items-center gap-1">
              <Input
                placeholder="Buscar por telefone..."
                value={phoneSearchInput}
                onChange={(e) => setPhoneSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePhoneSearch()}
                className="w-[180px]"
              />
              <Button variant="outline" size="icon" onClick={handlePhoneSearch}>
                <Phone className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabela de Logs - SEM coluna Tenant */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">Timestamp</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Operação</TableHead>
                  <TableHead>Direção</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Latência</TableHead>
                  <TableHead className="w-[60px]">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Carregando logs...
                    </TableCell>
                  </TableRow>
                ) : !logsResult?.logs.length ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nenhum log encontrado para os filtros aplicados
                    </TableCell>
                  </TableRow>
                ) : (
                  logsResult.logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs">
                        {format(new Date(log.created_at), 'dd/MM HH:mm:ss')}
                      </TableCell>
                      <TableCell>{renderProviderBadge(log.provider_slug)}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[150px] truncate" title={log.operation_type}>
                        {log.operation_type}
                      </TableCell>
                      <TableCell>{renderDirectionBadge(log.direction)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{log.source_type}</Badge>
                      </TableCell>
                      <TableCell>{renderStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => setSelectedLog(log)}
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          {logsResult && logsResult.total > 0 && (
            <PaginationFooter
              currentPage={page}
              pageSize={pageSize}
              totalItems={logsResult.total}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes - Completo com todas entidades relacionadas */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Detalhes da Requisição</span>
              {selectedLog && renderStatusBadge(selectedLog.status)}
            </DialogTitle>
            {/* Exibe ID do log ao invés de data */}
            <DialogDescription className="font-mono text-xs">
              {selectedLog?.id}
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Informações</TabsTrigger>
                <TabsTrigger value="request">Request</TabsTrigger>
                <TabsTrigger value="response">Response</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Provider</p>
                    <p className="font-medium">{selectedLog.provider_slug}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Operação</p>
                    <p className="font-medium font-mono text-sm">{selectedLog.operation_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Direção</p>
                    <p className="font-medium">{selectedLog.direction === 'inbound' ? 'Entrada' : 'Saída'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Origem</p>
                    <p className="font-medium">{selectedLog.source_type}</p>
                  </div>
                  {/* Endpoint sempre visível */}
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">Endpoint</p>
                    <p className="font-medium font-mono text-xs break-all">{selectedLog.endpoint || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Latência</p>
                    <p className="font-medium">{selectedLog.response_time_ms ? `${selectedLog.response_time_ms}ms` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status HTTP</p>
                    <p className="font-medium">{selectedLog.response_status || '-'}</p>
                  </div>
                  {/* External ID */}
                  <div>
                    <p className="text-sm text-muted-foreground">External ID</p>
                    <p className="font-medium font-mono text-xs break-all">{selectedLog.external_id || '-'}</p>
                  </div>
                  {/* IP Address */}
                  <div>
                    <p className="text-sm text-muted-foreground">IP Address</p>
                    <p className="font-medium font-mono text-xs">{selectedLog.ip_address || '-'}</p>
                  </div>
                  {/* Erro (condicional) */}
                  {selectedLog.error_message && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Erro</p>
                      <p className="font-medium text-destructive text-sm">{selectedLog.error_message}</p>
                      {selectedLog.error_code && (
                        <p className="text-xs text-muted-foreground">Código: {selectedLog.error_code}</p>
                      )}
                    </div>
                  )}
                  {/* Criado em */}
                  <div>
                    <p className="text-sm text-muted-foreground">Criado em</p>
                    <p className="font-medium">{format(new Date(selectedLog.created_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}</p>
                  </div>
                  {/* Completado em */}
                  <div>
                    <p className="text-sm text-muted-foreground">Completado em</p>
                    <p className="font-medium">{selectedLog.completed_at ? format(new Date(selectedLog.completed_at), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR }) : '-'}</p>
                  </div>
                </div>

                {/* Seção Entidades Relacionadas */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3">Entidades Relacionadas</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Tenant:</span>
                      <span className="font-mono break-all">{selectedLog.tenant_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Inbox:</span>
                      <span className="font-mono break-all">{selectedLog.inbox_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Mensagem:</span>
                      <span className="font-mono break-all">{selectedLog.message_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Conversa:</span>
                      <span className="font-mono break-all">{selectedLog.conversation_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Contato:</span>
                      <span className="font-mono break-all">{selectedLog.contact_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Campanha:</span>
                      <span className="font-mono break-all">{selectedLog.campaign_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Flow:</span>
                      <span className="font-mono break-all">{selectedLog.flow_id || '-'}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-muted-foreground min-w-[80px]">Correlation:</span>
                      <span className="font-mono break-all">{selectedLog.correlation_id || '-'}</span>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="request">
                <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                    {selectedLog.request_payload 
                      ? JSON.stringify(selectedLog.request_payload, null, 2)
                      : 'Sem payload de request'}
                  </pre>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="response">
                <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                    {selectedLog.response_payload 
                      ? JSON.stringify(selectedLog.response_payload, null, 2)
                      : 'Sem payload de response'}
                  </pre>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
